/**
 * LA DOSIS POR HECTÁREA DEL CUADERNO (CQ2): «kg/ha» o «l/ha», como pide el 3.1.
 *
 * T1: `treatmentDosing` es la ÚNICA conversión de una dosis a cantidad ABSOLUTA.
 * Aquí se hace solo el camino de vuelta: de la cantidad absoluta aplicada en el
 * parte a «por hectárea», dividiendo por el área tratada y pasando mL→l o g→kg
 * con `unitConversion` (la regla única de unidades).
 *
 *   1. Lo normal: la línea trae la cantidad aplicada (`amount_used` + unidad).
 *   2. Si no la trae, pero trae la dosis escrita (0,8 L/ha, 150 mL/100 L…), se
 *      pide la cantidad absoluta a `resolveProductAmount` —la misma cuenta que
 *      hizo la tarea al completarse— y se sigue por 1.
 *   3. Lo que no se puede pasar a kg o l sin inventar (unidades, envases, familias
 *      que no casan) o no tiene superficie → hueco con su motivo. Nunca un número.
 */
import { convertQuantity, normalizeUnit } from "@/lib/unitConversion";
import { resolveCaldoLiters, resolveProductAmount } from "@/lib/treatmentDosing";
import type { MotivoHueco } from "./modeloOficial";

export interface DosisEntrada {
  cantidad?: number | null;
  unidad?: string | null;
  dosisValor?: number | null;
  dosisUnidad?: string | null;
  agua?: number | null;
  aguaUnidad?: string | null;
  /** Unidad de stock del producto (para resolver la dosis escrita). */
  unidadStock?: string | null;
  /** Área tratada en m². */
  areaM2?: number | null;
}

export type DosisPorHa =
  | { ok: true; valor: number; unidad: "kg/ha" | "l/ha"; desde: "cantidad" | "dosis" }
  | { ok: false; motivo: Extract<MotivoHueco, "falta" | "no_convertible" | "sin_superficie"> };

/** Tres decimales: 0,125 l/ha se lee; el ruido de coma flotante no. */
const r3 = (n: number) => Math.round(n * 1000) / 1000;

/** Cantidad absoluta → kg o l. null si no es masa ni volumen (unidades, envases, desconocida). */
export function aKgOLitros(cantidad: number, unidad: string | null | undefined): { valor: number; unidad: "kg" | "l" } | null {
  const u = normalizeUnit(unidad);
  if (u === "L" || u === "ml") {
    const l = convertQuantity(cantidad, u, "L");
    return l == null ? null : { valor: l, unidad: "l" };
  }
  if (u === "kg" || u === "g") {
    const kg = convertQuantity(cantidad, u, "kg");
    return kg == null ? null : { valor: kg, unidad: "kg" };
  }
  return null;
}

export function dosisPorHa(e: DosisEntrada): DosisPorHa {
  const areaM2 = Number(e.areaM2);
  const hayArea = Number.isFinite(areaM2) && areaM2 > 0;

  let cantidad = e.cantidad != null && Number(e.cantidad) > 0 ? Number(e.cantidad) : null;
  let unidad = e.unidad ?? null;
  let desde: "cantidad" | "dosis" = "cantidad";

  if (cantidad == null && e.dosisValor != null && Number(e.dosisValor) > 0 && e.dosisUnidad) {
    // T1: la cantidad absoluta la calcula treatmentDosing, no el cuaderno.
    const r = resolveProductAmount({
      value: Number(e.dosisValor),
      unit: e.dosisUnidad,
      areaM2: hayArea ? areaM2 : 0,
      caldoL: resolveCaldoLiters(e.agua ?? null, e.aguaUnidad ?? null, hayArea ? areaM2 : 0),
      stockUnit: e.unidadStock || e.unidad || "",
    });
    if (r.error === "AREA_REQUIRED") return { ok: false, motivo: "sin_superficie" };
    if (r.error === "UNIT_PRODUCT_MISMATCH") return { ok: false, motivo: "no_convertible" };
    if (r.error) return { ok: false, motivo: "falta" };
    cantidad = r.amount;
    unidad = r.amountUnit;
    desde = "dosis";
  }

  if (cantidad == null) return { ok: false, motivo: "falta" };
  const abs = aKgOLitros(cantidad, unidad);
  if (!abs) return { ok: false, motivo: "no_convertible" };
  if (!hayArea) return { ok: false, motivo: "sin_superficie" };
  return { ok: true, valor: r3(abs.valor / (areaM2 / 10000)), unidad: abs.unidad === "kg" ? "kg/ha" : "l/ha", desde };
}
